import { cn } from '@/utils/styles';
import { Card } from '@design-system/Card';
import { Skeleton } from '@design-system/Skeleton';
import { List, ListItem } from '@design-system/List';
import { useUserDetails } from '@/hooks/useUserDetails';

export interface SettingsPageAccountSectionProps
  extends React.ComponentProps<'div'> {
  className?: string;
  'data-testid'?: string;
}

type IUserData = {
  label: string;
  value?: string | number;
  isLoading?: boolean;
};

const renderUserData = (userData: IUserData[]) => {
  const renderedUserData = userData.map((data) => {
    const itemTestId = `settingsAccount-item-${data.label.toLowerCase().replace(/\s+/g, '')}`;
    
    return (
      <ListItem key={data.label} hideIndicator data-testid={itemTestId}>
        <span data-testid={`${itemTestId}-label`}>{data.label}</span>

        {data.isLoading ? (
          <Skeleton className="ml-auto w-full h-4 max-w-24" data-testid={`${itemTestId}-skeleton`} />
        ) : (
          <span className="ml-auto text-secondary truncate" data-testid={`${itemTestId}-value`}>
            <abbr title={String(data.value)} className="no-underline">
              {data.value}
            </abbr>
          </span>
        )}
      </ListItem>
    );
  });

  return renderedUserData;
};

export const SettingsPageAccountSection = ({
  className,
  'data-testid': testId,
  ...props
}: SettingsPageAccountSectionProps) => {
  const { fullName, firstName, lastName, email } = useUserDetails();
  const fullNameLoading = !fullName || !firstName || !lastName;
  const emailLoading = !email;

  const userData = [
    {
      label: 'Name',
      value: fullName || `${firstName} ${lastName}`.trim(),
      isLoading: fullNameLoading,
    },
    {
      label: 'Email',
      value: email,
      isLoading: emailLoading,
    },
  ] satisfies IUserData[];

  return (
    <div className={cn('flex flex-col', className)} data-testid={testId} {...props}>
      <span className="typography-h2 text-primary mb-6" data-testid="settingsAccount-title">Account</span>

      <div className="flex flex-col gap-2" data-testid="settingsAccount-content">
        <Card className="overflow-hidden" data-testid="settingsAccount-card">
          <List data-testid="settingsAccount-list">{renderUserData(userData)}</List>
        </Card>
      </div>
    </div>
  );
};
